/**
 * <PedagogicalCanvas /> — Affiche une fiche séance suivant l'une des
 * deux trames pédagogiques officielles ECSR :
 *
 *  - trameId="salle"    → Trame Cours en salle (ECSR salle, 7 étapes)
 *  - trameId="conduite" → Trame Leçon de conduite (CCS 2R, 4 phases, 9 étapes)
 *
 * Props :
 *  - trameId     : 'salle' | 'conduite' (défaut: 'conduite')
 *  - title       : titre de la séance (string)
 *  - kicker      : libellé contexte (ex: "Cours plateau · 1h")
 *  - meta        : { duree, niveau, lieu, materiel } — affichés en tête
 *  - steps       : objet { [stepId]: { content, items?, notes? } } — données par étape
 *  - onPresentToggle : callback pour activer le mode présentation (Phase 3)
 *  - testid      : préfixe pour data-testid
 *  - accent      : couleur d'accent (par défaut celle de la trame)
 *  - showHeader  : afficher ou non l'en-tête
 *
 * Les étapes vides (sans content) sont affichées en grisé "À compléter".
 */
import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Maximize2,
  Clock,
  GraduationCap,
  MapPin,
  Package,
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { getTrameById, getStepsByPhase } from '../../data/teacher/canevasMeta';

function MetaPill({ icon: Icon, label, value, color }) {
  if (!value) return null;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border"
      style={{ borderColor: `${color}33`, color, background: `${color}0d` }}
      data-testid={`meta-${label}`}
    >
      <Icon className="w-3 h-3" /> {value}
    </span>
  );
}

function StepBlock({ step, data, defaultOpen = true, accent }) {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = step.icon;
  const isEmpty = !data || (!data.content && !data.items?.length);
  const stepColor = accent || step.color;

  return (
    <section
      className="rounded-xl border border-border bg-card overflow-hidden"
      style={{ borderLeft: `4px solid ${stepColor}` }}
      data-testid={`canevas-step-block-${step.id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/30 transition-colors"
        aria-expanded={open}
        data-testid={`canevas-step-toggle-${step.id}`}
      >
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center text-white font-black flex-shrink-0 text-sm"
          style={{ background: stepColor }}
        >
          {step.n}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Icon className="w-4 h-4 flex-shrink-0" style={{ color: stepColor }} />
            <h3 className="text-sm sm:text-base font-black tracking-tight">{step.label}</h3>
            {isEmpty && (
              <Badge variant="outline" className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
                À compléter
              </Badge>
            )}
          </div>
          <p className="text-[11px] sm:text-xs text-muted-foreground italic mt-0.5">
            {step.intent}
          </p>
        </div>
        {open ? (
          <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        )}
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-border/40 bg-background/40">
          {isEmpty ? (
            <div className="text-xs italic text-muted-foreground py-3">
              <span className="font-bold text-foreground/80">À compléter — </span>
              {step.description}
              {step.examples?.length > 0 && (
                <>
                  <div className="mt-2 text-[11px] font-black uppercase tracking-wider text-muted-foreground">
                    Exemples
                  </div>
                  <ul className="mt-1 space-y-1 text-xs not-italic">
                    {step.examples.map((ex, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <span style={{ color: stepColor }}>•</span>
                        <span>{ex}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          ) : (
            <>
              {data.content && (
                <div
                  className="text-sm leading-relaxed text-foreground/90"
                  dangerouslySetInnerHTML={{ __html: data.content }}
                />
              )}
              {data.items?.length > 0 && (
                <ul className="space-y-1.5 text-sm" data-testid={`canevas-step-items-${step.id}`}>
                  {data.items.map((it, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span
                        className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
                        style={{ background: stepColor }}
                        aria-hidden="true"
                      />
                      <span
                        className="leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: typeof it === 'string' ? it : '' }}
                      />
                    </li>
                  ))}
                </ul>
              )}
              {data.notes && (
                <div
                  className="text-xs italic text-muted-foreground pt-2 border-t border-border/40"
                  dangerouslySetInnerHTML={{ __html: `<strong class="not-italic">Note moniteur — </strong>${data.notes}` }}
                />
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

export default function PedagogicalCanvas({
  trameId = 'conduite',
  title,
  kicker,
  meta = {},
  steps = {},
  onPresentToggle,
  testid = 'pedagogical-canvas',
  accent,
  showHeader = true,
}) {
  const trame = getTrameById(trameId);
  if (!trame) return null;
  const trameAccent = accent || trame.accent;
  const phaseGroups = getStepsByPhase(trameId); // null si la trame n'a pas de phases (salle)

  return (
    <article className="space-y-4" data-testid={testid}>
      {showHeader && (
        <header
          className="mc-card p-5 sm:p-7 relative overflow-hidden"
          style={{ borderLeft: `6px solid ${trameAccent}` }}
        >
          <div
            className="absolute -top-16 -right-16 w-72 h-72 rounded-full blur-3xl opacity-20 pointer-events-none"
            style={{ background: trameAccent }}
            aria-hidden="true"
          />
          <div className="relative">
            <div
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-widest mb-2"
              style={{ background: `${trameAccent}1a`, color: trameAccent }}
            >
              {kicker || `Trame ${trame.label}`}
            </div>
            {title && (
              <h1
                className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-[1.1]"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                data-testid={`${testid}-title`}
              >
                {title}
              </h1>
            )}

            {/* Méta */}
            <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`${testid}-meta`}>
              <MetaPill icon={Clock} label="duree" value={meta.duree} color={trameAccent} />
              <MetaPill icon={GraduationCap} label="niveau" value={meta.niveau} color={trameAccent} />
              <MetaPill icon={MapPin} label="lieu" value={meta.lieu} color={trameAccent} />
              <MetaPill icon={Package} label="materiel" value={meta.materiel} color={trameAccent} />
            </div>

            {/* Actions */}
            {onPresentToggle && (
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={onPresentToggle}
                  className="gap-1.5"
                  style={{ background: trameAccent }}
                  data-testid={`${testid}-present-toggle`}
                >
                  <Maximize2 className="w-3.5 h-3.5" /> Mode présentation (pour la classe)
                </Button>
              </div>
            )}
          </div>
        </header>
      )}

      {/* Affichage par phases si la trame est groupée (conduite),
          sinon en liste simple (salle). */}
      {phaseGroups ? (
        <div className="space-y-5" data-testid={`${testid}-steps`}>
          {phaseGroups.map((phase) => (
            <div key={phase.id} data-testid={`canevas-phase-${phase.id}`}>
              <div className="flex items-baseline gap-2 mb-2">
                <span
                  className="text-[10px] font-black uppercase tracking-[0.18em] px-2 py-0.5 rounded-full"
                  style={{ background: `${phase.color}1a`, color: phase.color }}
                >
                  {phase.label}
                </span>
              </div>
              <div className="space-y-2.5">
                {phase.steps.map((step) => (
                  <StepBlock
                    key={step.id}
                    step={step}
                    data={steps[step.id]}
                    defaultOpen={!!steps[step.id]?.content || !!steps[step.id]?.items?.length}
                    accent={step.color}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3" data-testid={`${testid}-steps`}>
          {trame.steps.map((step) => (
            <StepBlock
              key={step.id}
              step={step}
              data={steps[step.id]}
              defaultOpen={!!steps[step.id]?.content || !!steps[step.id]?.items?.length}
              accent={step.color}
            />
          ))}
        </div>
      )}
    </article>
  );
}
